import React, { useState } from 'react';
import { User } from '../types';
import { Sparkles, Save, Upload, Loader2, AlertCircle } from 'lucide-react';
import { supabase, supabase_update_client_branding } from '../services/supabaseService';
import { ai, generateAIAvatar, AI_PROXY_CHAT_MODEL } from '../services/geminiService'; // Direct import to use ai.models

interface AIRecruiterSettingsProps {
    user: User;
}

const AIRecruiterSettings: React.FC<AIRecruiterSettingsProps> = ({ user }) => {
    // Fallback to empty if not set
    const initialAI = user.branding?.aiRecruiter || { name: 'Froukje', description: 'Jouw digitale AI recruiter en data analist.', avatarUrl: '' };

    const [name, setName] = useState(initialAI.name);
    const [description, setDescription] = useState(initialAI.description);
    const [avatarUrl, setAvatarUrl] = useState(initialAI.avatarUrl || '');

    const [isGenerating, setIsGenerating] = useState(false);
    const [isGeneratingAvatar, setIsGeneratingAvatar] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    const handleGenerateBio = async () => {
        if (!name) return;
        setIsGenerating(true);
        setMessage(null);
        try {
            const prompt = `Je bent een expert copywriter. Schrijf een korte, vlotte en enthousiaste bio (max 2 zinnen) voor een AI recruiter genaamd "${name}" die werkt voor het bedrijf "${user.clientName || 'ons bedrijf'}". Spreek in de ik-vorm.`;

            const response = await ai.models.generateContent({
                model: AI_PROXY_CHAT_MODEL,
                contents: prompt,
            });

            if (response.text) {
                setDescription(response.text.trim().replace(/^["']|["']$/g, '')); // Remove surrounding quotes if any
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Kon geen bio genereren.' });
        } finally {
            setIsGenerating(false);
        }
    };

    const handleGenerateAvatar = async () => {
        if (!name) return;
        setIsGeneratingAvatar(true);
        setMessage(null);
        try {
            const base64Avatar = await generateAIAvatar(name, user.clientName);
            setAvatarUrl(base64Avatar);
            setMessage({ type: 'success', text: 'Nieuwe avatar gegenereerd! Vergeet niet op te slaan.' });
        } catch (error: any) {
            setMessage({ type: 'error', text: error.message || 'Kon geen avatar genereren.' });
        } finally {
            setIsGeneratingAvatar(false);
        }
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !user.clientId) return;

        setIsUploading(true);
        setMessage(null);
        try {
            const fileExt = file.name.split('.').pop();
            const fileName = `ai_avatar_${user.clientId}_${Math.random()}.${fileExt}`;
            const filePath = `avatars/${fileName}`;

            const { error: uploadError } = await supabase.storage
                .from('logos') // Reusing the logos bucket
                .upload(filePath, file);

            if (uploadError) throw uploadError;

            const { data } = supabase.storage.from('logos').getPublicUrl(filePath);
            setAvatarUrl(data.publicUrl);
        } catch (error: any) {
            setMessage({ type: 'error', text: 'Kon afbeelding niet uploaden. ' + error.message });
        } finally {
            setIsUploading(false);
        }
    };

    const handleSave = async () => {
        if (!user.clientId) {
            setMessage({ type: 'error', text: 'Geen klant ID gevonden.' });
            return;
        }

        setIsSaving(true);
        setMessage(null);

        try {
            const currentBranding = user.branding || {};
            const newBranding = {
                ...currentBranding,
                aiRecruiter: {
                    name,
                    description,
                    avatarUrl
                }
            };

            await supabase_update_client_branding(user.clientId, newBranding);

            // Note: In an ideal world we'd update the global User context here, 
            // but a page reload or relying on the parent to refetch is acceptable for now.
            setMessage({ type: 'success', text: 'Instellingen opgeslagen! Herlaad de pagina om de wijzigingen te zien.' });
        } catch (error: any) {
            setMessage({ type: 'error', text: 'Fout bij opslaan: ' + error.message });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="space-y-8 p-6 lg:p-10 max-w-3xl mx-auto animate-fadeIn pb-24">
            <div>
                <h1 className="text-xl font-black text-slate-900 tracking-tight leading-tight flex items-center gap-2">
                    <Sparkles className="text-[#e51b23] shrink-0" size={24} /> AI Recruiter (White-label)
                </h1>
                <p className="text-xs text-slate-500 font-medium mt-1">Pas de naam, foto en bio aan van jouw persoonlijke AI assistent.</p>
            </div>

            {message && (
                <div className={`p-4 rounded-xl text-xs font-bold flex items-center gap-3 ${message.type === 'success' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-red-50 text-red-600 border border-red-100'
                    }`}>
                    <AlertCircle size={16} /> {message.text}
                </div>
            )}

            <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm p-8 space-y-6">

                {/* Avatar */}
                <div className="flex items-center gap-6">
                    <div className="relative w-24 h-24 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center border border-slate-200 shadow-inner overflow-hidden group shrink-0">
                        {avatarUrl ? (
                            <img src={avatarUrl} alt="AI Avatar" className="w-full h-full object-cover" />
                        ) : (
                            <Sparkles size={32} className="text-slate-300" />
                        )}
                        <label className="absolute inset-0 bg-slate-900/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center cursor-pointer text-white">
                            {isUploading ? <Loader2 size={20} className="animate-spin" /> : <Upload size={20} />}
                            <span className="text-[9px] font-black uppercase tracking-widest mt-1">Wijzig</span>
                            <input type="file" className="hidden" accept="image/*" onChange={handleFileUpload} disabled={isUploading} />
                        </label>
                    </div>
                    <div>
                        <h3 className="font-black text-slate-900 text-sm uppercase mb-1">Avatar Afbeelding</h3>
                        <p className="text-xs text-slate-500 mb-3">Upload een (vierkante) profielfoto voor je ideale AI recruiter.</p>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={handleGenerateAvatar}
                                disabled={isGeneratingAvatar || !name}
                                className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-[#e51b23] hover:text-pink-600 disabled:opacity-50 transition-colors bg-[#e51b23]/10 px-3 py-1.5 rounded-lg w-fit"
                            >
                                {isGeneratingAvatar ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                                Genereer met AI
                            </button>
                            <div className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">of klik op de foto</div>
                        </div>
                    </div>
                </div>

                <hr className="border-slate-100" />

                {/* Name */}
                <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Naam AI Recruiter</label>
                    <input
                        type="text"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        placeholder="bijv. Froukje, Lisa, Alex..."
                        className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-[#e51b23]/20 focus:border-[#e51b23] transition-all"
                    />
                </div>

                {/* Description */}
                <div className="space-y-2 relative">
                    <div className="flex items-center justify-between ml-1 mb-1">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Korte Bio / Omschrijving</label>
                        <button
                            onClick={handleGenerateBio}
                            disabled={isGenerating || !name}
                            className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-[#e51b23] hover:text-pink-600 disabled:opacity-50 transition-colors bg-[#e51b23]/10 px-3 py-1.5 rounded-lg"
                        >
                            {isGenerating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                            Genereer met AI
                        </button>
                    </div>
                    <textarea
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        placeholder="Een korte introductie voor kandidaten en recruiters..."
                        rows={3}
                        className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-[#e51b23]/20 focus:border-[#e51b23] transition-all resize-none"
                    />
                </div>

                {/* Save Button */}
                <div className="pt-6">
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="w-full sm:w-auto bg-slate-900 text-white px-8 py-4 rounded-xl font-black text-xs uppercase tracking-widest shadow-lg hover:bg-black transition-all flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50"
                    >
                        {isSaving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                        Wijzigingen Opslaan
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AIRecruiterSettings;
